import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Card,
  Flex,
  FullscreenTakeover,
  QueryLoader,
  Table,
  TableCell,
  TableRow,
  Text,
  toast,
} from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { client, withClientContext } from "~/apollo/client";
import { globalEnv } from "~/global-env";
import { useGetClientNameByIdQuery } from "~/graphql/mainHooks";
import {
  ComplianceCaseTypeEnum,
  ComplianceExternalObjectType,
} from "~/graphql/supergraphEnums";
import {
  useGetIndividualPositionDetailsQuery,
  useLinkOnboardingCaseMutation,
  useUpdatePositionCaseMapMutation,
} from "~/graphql/supergraphHooks";
import { MAIN_SERVICE } from "~/pages/Compliance/utils/constants";
import { toDbId, toGlobalId } from "~/utils/graphql-helpers";
import ProposedCasesGrid from "./ProposedCasesGrid";
import { casesListContainer, fullScreenFooter, positionToLinkText } from "./styles_vanilla.css";
import type { ExternalObjectInput } from "~/graphql/supergraphTypes";

export type LinkCaseSubjectKind = "position" | "entity" | "client";

type Props = {
  subjectKind?: LinkCaseSubjectKind;
};

const SUBJECT_COPY: Record<
  LinkCaseSubjectKind,
  { title: string; cardLabel: string; applyLabel: string; successToast: string }
> = {
  position: {
    title: "Select case to link to this position",
    cardLabel: "POSITION TO LINK",
    applyLabel: "Link case",
    successToast: "Position linked successfully",
  },
  entity: {
    title: "Select case to link to this entity",
    cardLabel: "ENTITY TO LINK",
    applyLabel: "Apply",
    successToast: "Case linked successfully",
  },
  client: {
    title: "Select case to link to this client",
    cardLabel: "CLIENT TO LINK",
    applyLabel: "Apply",
    successToast: "Case linked successfully",
  },
};

const caseTypeForSubject = (subjectKind: LinkCaseSubjectKind): string => {
  if (subjectKind === "entity") return ComplianceCaseTypeEnum.EntityOnboarding;
  if (subjectKind === "client") return ComplianceCaseTypeEnum.ClientOnboarding;
  return "investor_onboarding";
};

const objectIdFromParam = (id: string): string => (/^\d+$/.test(id) ? id : String(toDbId(id)));

const arenaIdFromParam = (id: string): number => (/^\d+$/.test(id) ? Number(id) : toDbId(id));

const buildLinkExternalObject = (args: {
  subjectKind: LinkCaseSubjectKind;
  entityId: string;
  entityName: string;
  clientId: string;
  clientName: string;
}): ExternalObjectInput | null => {
  const { subjectKind, entityId, entityName, clientId, clientName } = args;
  if (subjectKind === "entity") {
    if (!entityId) return null;
    return {
      objectId: objectIdFromParam(entityId),
      objectTypeEnum: ComplianceExternalObjectType.Account,
      service: MAIN_SERVICE,
      name: entityName,
    };
  }
  if (!clientId) return null;
  return {
    objectId: objectIdFromParam(clientId),
    objectTypeEnum: ComplianceExternalObjectType.Arena,
    service: MAIN_SERVICE,
    name: clientName,
  };
};

const exitPathForSubject = (
  subjectKind: LinkCaseSubjectKind,
  params: { arenaIds: string; entityId: string; clientId: string },
): string => {
  if (subjectKind === "entity") {
    const search = params.clientId ? `?clientId=${params.clientId}` : "";
    return `${globalEnv.complianceRoute}/entities${search}`;
  }
  if (subjectKind === "client") {
    return `${globalEnv.complianceRoute}/clients`;
  }
  return `${globalEnv.complianceRoute}/positions?arenaIds=${params.arenaIds}&entityId=${params.entityId}`;
};

const SubjectRows: React.FC<{
  subjectKind: LinkCaseSubjectKind;
  positionAccountName?: string;
  positionEntityName?: string;
  positionClientName?: string;
  entityName: string;
  clientName: string;
}> = ({
  subjectKind,
  positionAccountName,
  positionEntityName,
  positionClientName,
  entityName,
  clientName,
}) => {
  if (subjectKind === "position") {
    return (
      <>
        <TableRow>
          <TableCell header={true}>Position</TableCell>
          <TableCell>
            <Flex flexDirection="row" gap={Sizes.XS}>
              <Text variant="strong-md" asSpan>
                {positionAccountName}
              </Text>
              <Text variant="secondary-md" asSpan>
                in
              </Text>
              <Text variant="strong-md" asSpan>
                {positionEntityName}
              </Text>
            </Flex>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell header={true}>Client</TableCell>
          <TableCell>
            <Text variant="regular-md">{positionClientName}</Text>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell header={true}>Fund</TableCell>
          <TableCell>
            <Text variant="regular-md">{positionEntityName}</Text>
          </TableCell>
        </TableRow>
      </>
    );
  }
  if (subjectKind === "entity") {
    return (
      <>
        <TableRow>
          <TableCell header={true}>Client</TableCell>
          <TableCell>
            <Text variant="regular-md">{clientName || "--"}</Text>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell header={true}>Entity</TableCell>
          <TableCell>
            <Text variant="regular-md">{entityName || "--"}</Text>
          </TableCell>
        </TableRow>
      </>
    );
  }
  return (
    <TableRow>
      <TableCell header={true}>Client</TableCell>
      <TableCell>
        <Text variant="regular-md">{clientName || "--"}</Text>
      </TableCell>
    </TableRow>
  );
};

const CasesListFullScreen: React.FC<Props> = ({ subjectKind = "position" }) => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history.location.search);
  const arenaIds = queryParams.get("arenaIds") || "";
  const globalArenaIds = arenaIds
    ? arenaIds
        .split(",")
        .filter(Boolean)
        .map((id) => toGlobalId("ArenaObjectType", Number(id)))
    : [];
  const entityId = queryParams.get("entityId") || "";
  const entityName = queryParams.get("entityName") || "";
  const clientId = queryParams.get("clientId") || "";
  const clientNameParam = queryParams.get("clientName") || "";
  const globalEntityId = toGlobalId("AccountObjectType", Number(entityId));
  const positionId = queryParams.get("positionId") || "";
  const globalPositionId = toGlobalId("PositionObjectType", Number(positionId));
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const copy = SUBJECT_COPY[subjectKind];
  const caseType = caseTypeForSubject(subjectKind);
  const isPosition = subjectKind === "position";

  const handleSelection = (caseId: number | null) => {
    setSelectedCaseId(caseId);
  };

  const onExit = () => {
    history.push(exitPathForSubject(subjectKind, { arenaIds, entityId, clientId }));
  };

  const { data: clientNameData } = useGetClientNameByIdQuery({
    variables: {
      arenaIds: clientId ? [toGlobalId("ArenaObjectType", Number(clientId))] : [],
    },
    context: withClientContext("staff"),
    skip: isPosition || !clientId || !!clientNameParam,
  });
  const resolvedClientName =
    clientNameParam || clientNameData?.arenas?.edges?.[0]?.node?.name || "";

  const positionDetails = useGetIndividualPositionDetailsQuery({
    variables: {
      positionFilters: {
        arenaIds: globalArenaIds,
        entityIds: [globalEntityId],
        positionIds: [globalPositionId],
        includeFundAdminEntities: false,
      },
    },
    fetchPolicy: "no-cache",
    skip: !isPosition,
  });

  const positionData = positionDetails.data?.positions?.edges[0]?.node;
  const arenaIdFromUrl = arenaIds.split(",").filter(Boolean)[0];
  const positionArenaId = arenaIdFromUrl ? arenaIdFromParam(arenaIdFromUrl) : undefined;

  const [updatePositionMap, { loading: linkingPosition }] = useUpdatePositionCaseMapMutation({
    variables: {
      positionId: positionId,
      caseId: selectedCaseId,
    },
    refetchQueries: ["GetCompliancePositionsList"],
    onError: (e) => {
      toast.error(e.message);
    },
    onCompleted: () => {
      toast.success(copy.successToast);
      void client.refetchQueries({
        include: "active",
      });
      onExit();
    },
  });

  const [linkOnboardingCase, { loading: linkingOnboarding }] = useLinkOnboardingCaseMutation({
    onError: (e) => {
      toast.error(e.message);
    },
    onCompleted: () => {
      toast.success(copy.successToast);
      onExit();
    },
  });

  const handleClick = async () => {
    if (isPosition) {
      await updatePositionMap();
      return;
    }
    if (selectedCaseId === null) return;
    if (!clientId) {
      toast.error("Missing client identifier for linking");
      return;
    }
    const externalObject = buildLinkExternalObject({
      subjectKind,
      entityId,
      entityName,
      clientId,
      clientName: resolvedClientName,
    });
    if (!externalObject) {
      toast.error("Missing subject identifiers for linking");
      return;
    }
    await linkOnboardingCase({
      variables: {
        caseId: selectedCaseId,
        arenaId: arenaIdFromParam(clientId),
        externalObject,
      },
    });
  };

  const pickerBody = (
    <Flex flexDirection="column" className={casesListContainer} gap={Sizes.LG}>
      <Card padding="md">
        <Flex flexDirection="column">
          <Text className={positionToLinkText} variant="secondary-md">
            {copy.cardLabel}
          </Text>
          <Table variant="muted" hasBottomBorder={false} condensed>
            <SubjectRows
              subjectKind={subjectKind}
              positionAccountName={positionData?.account?.name}
              positionEntityName={positionData?.investmentEntity?.name}
              positionClientName={positionData?.arena?.name}
              entityName={entityName}
              clientName={resolvedClientName}
            />
          </Table>
        </Flex>
      </Card>
      <ProposedCasesGrid
        onRowSelected={handleSelection}
        arenaId={isPosition ? positionArenaId : undefined}
        entityName={isPosition ? (positionData?.investmentEntity?.name ?? "") : undefined}
        caseType={caseType}
        clientObjectId={
          subjectKind === "entity" && clientId ? objectIdFromParam(clientId) : undefined
        }
        positionId={isPosition ? positionId : undefined}
      />
    </Flex>
  );

  return (
    <FullscreenTakeover>
      <FullscreenTakeover.Header onExit={onExit} title={copy.title} />
      <FullscreenTakeover.Body>
        {isPosition ? (
          <QueryLoader result={positionDetails}>{pickerBody}</QueryLoader>
        ) : (
          pickerBody
        )}
      </FullscreenTakeover.Body>
      <FullscreenTakeover.Footer>
        <Flex
          className={fullScreenFooter}
          justifyContent="flex-end"
          gap={Sizes.SM}
          alignItems="center"
        >
          <Button type="button" variant="neutral" onClick={onExit}>
            Close
          </Button>
          <Button
            variant="primary"
            disabled={selectedCaseId === null}
            onClick={handleClick}
            isLoading={isPosition ? linkingPosition : linkingOnboarding}
          >
            {copy.applyLabel}
          </Button>
        </Flex>
      </FullscreenTakeover.Footer>
    </FullscreenTakeover>
  );
};

export default CasesListFullScreen;
